import { useAuthenticator } from "@aws-amplify/ui-react";
import { Icon } from "@iconify/react";
import { useFormik } from "formik";
import isEqual from "lodash.isequal";
import { BlockUI } from "primereact/blockui";
import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Divider } from "primereact/divider";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { classNames } from "primereact/utils";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { v4 as uuid } from "uuid";
import { capitalizeWord, getUpdateRemoveParams, objectHasKeys, updateObjectProperty } from "../../../Utils";
import { checkComponentPermissions } from "../../../data/Permissions";
import { getAdditionalTopBarComponentsV2 } from "../../../data/TabsData";
import { useCallbackPrompt } from "../../../hooks/useCallbackPrompt";
import TenantService from "../../../services/TenantService/TenantService";
import UserService from "../../../services/UserService/UserService";
import Forbidden from "../../Auth/Forbidden";
import "./Overhead.css";

const Overhead = ({ setTopbarAdditionalComponents, setOption }) => {
    const toast = useRef();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const { user } = useAuthenticator((context) => [context.user]);

    const [loading, setLoading] = useState(false);
    const [changesDetected, setChangesDetected] = useState(false);
    const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(changesDetected);

    const [tenant, setTenant] = useState();
    const [originalTenant, setOriginalTenant] = useState();
    const [totalUsers, setTotalUsers] = useState(0);
    const [calculatedOverhead, setCalculatedOverhead] = useState(0);

    const userService = new UserService();
    const tenantService = new TenantService();

    useEffect(() => {
        setTopbarAdditionalComponents(getAdditionalTopBarComponentsV2("tenant_overhead", navigate, ""));
    }, []);

    useEffect(() => {
        setOption("tenant_overhead");
    }, []);

    useEffect(() => {
        setLoading(true);
        if (user && user.attributes && user.attributes["custom:tenant_id"]) {
            let calls = [];
            calls.push(
                tenantService
                    .getTenant(user.attributes["custom:tenant_id"])
                    .then((data) => {
                        if (data) {
                            formik.setValues(data);
                            setTenant(data);
                            setOriginalTenant(JSON.parse(JSON.stringify(data)));
                        }
                        return data;
                    })
                    .catch((error) => {
                        console.log(error);
                        toast?.current?.show({ severity: "error", summary: t("label.error"), detail: error?.response?.data?.message, life: 5000 });
                    })
            );
            calls.push(
                userService
                    .queryUsers({ limit: 1 })
                    .then((data) => {
                        setTotalUsers(data?.total ? data?.total : 0);
                        return data;
                    })
                    .catch((error) => {
                        console.log(error);
                        toast?.current?.show({ severity: "error", summary: t("label.error"), detail: error?.response?.data?.message, life: 5000 });
                    })
            );

            Promise.all(calls).then(([tenantInfo, usersInfo]) => {
                calculateOverhead(tenantInfo?.weekly_hours, tenantInfo?.business_days, tenantInfo?.people, usersInfo?.total ? usersInfo?.total : 0);
                setChangesDetected(false);
                setLoading(false);
            });
        }
    }, []);

    const formik = useFormik({
        initialValues: {
            business_days: 0,
            weekly_hours: 0,
            overhead: 0,
            people: [],
        },
        validate: (data) => {
            let errors = {};

            // if (!data.commercial_name) {
            //     errors.commercial_name = t("label.field_required");
            // } else if (!data.name) {
            //     errors.name = t("label.field_required");
            // } else if (!data.surnames) {
            //     errors.surnames = t("label.field_required");
            // } else if (!data.email) {
            //     errors.email = t("label.field_required");
            // } else if (!data.phone) {
            //     errors.phone = t("label.field_required");
            // } else if (!data.overhead) {
            //     errors.overhead = t("label.field_required");
            // } else if (!data.currency) {
            //     errors.currency = t("label.field_required");
            // } else if (!data.cif) {
            //     errors.cif = t("label.field_required");
            // } else if (!data.social_reason) {
            //     errors.social_reason = t("label.field_required");
            // } else if (!data.country) {
            //     errors.country = t("label.field_required");
            // } else if (!data.province) {
            //     errors.province = t("label.field_required");
            // } else if (!data.postal_code) {
            //     errors.postal_code = t("label.field_required");
            // } else if (!data.city) {
            //     errors.city = t("label.field_required");
            // } else if (!data.address) {
            //     errors.address = t("label.field_required");
            // }

            return errors;
        },
        onSubmit: (data) => {
            setLoading(true);

            let newData = {
                ...data,
            };

            const updateData = getUpdateRemoveParams(originalTenant, newData);

            tenantService
                .updateTenant(updateData, tenant?.id)
                .then((data) => {
                    setLoading(false);
                    if (data) {
                        setTenant(data);
                        setOriginalTenant(JSON.parse(JSON.stringify(data)));
                    }
                    setChangesDetected(false);
                })
                .catch((error) => {
                    console.log(error);
                    setLoading(false);
                    toast?.current?.show({ severity: "error", summary: t("label.error"), detail: error?.response?.data?.message, life: 5000 });
                });
        },
    });

    const updatePeopleAttribute = (index, key, value) => {
        let newPeople = [];
        if (formik?.values?.people && formik?.values?.people?.length > 0 && formik?.values?.people[index]) {
            newPeople = [...formik?.values?.people];

            newPeople[index][key] = value;

            if (key === "commitment") {
                calculateOverhead(formik.values.weekly_hours, formik.values.business_days, newPeople, totalUsers);
            }

            updateTenantField("people", newPeople);
        }
    };

    const addPeople = () => {
        let newPeople = [];
        if (formik?.values?.people?.length > 0) {
            newPeople = [...formik?.values?.people];
        }

        newPeople.push({
            id: uuid(),
            full_name: "",
            job_title: "",
            commitment: 0,
            monthly_hours: 0,
            weekly_hours: 0,
        });

        updateTenantField("people", newPeople);
    };

    const peopleTemplate = (peopleParam) => {
        return (
            <div className="flex flex-column p-0">
                {peopleParam &&
                    peopleParam?.map((item, index) => {
                        return (
                            <div className="flex flex-column min-w-full overflow-auto">
                                <div className="flex align-items-center justify-content-start">
                                    <div className="flex align-items-center justify-content-start w-3 pr-5" style={{ minWidth: "15rem" }}>
                                        <div className="flex flex-column min-w-full">
                                            <div className="flex align-items-start justify-content-start">
                                                <label className="mousee-text font-x-small font-weight-semibold block pl-2">{t("overhead.page.full_name")}*</label>
                                            </div>
                                            <div className="flex align-items-center justify-content-center">
                                                <InputText id="full_name" name="full_name" placeholder={t("overhead.page.full_name")} value={item?.full_name} className={classNames("block min-w-full")} onChange={(e) => updatePeopleAttribute(index, "full_name", e.target.value)} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex align-items-center justify-content-start w-3 pr-5" style={{ minWidth: "15rem" }}>
                                        <div className="flex flex-column min-w-full">
                                            <div className="flex align-items-start justify-content-start">
                                                <label className="mousee-text font-x-small font-weight-semibold block pl-2">{t("label.job_title")}*</label>
                                            </div>
                                            <div className="flex align-items-center justify-content-center">
                                                <InputText id="job_title" name="job_title" placeholder={t("label.job_title")} value={item?.job_title} className={classNames("block min-w-full")} onChange={(e) => updatePeopleAttribute(index, "job_title", e.target.value)} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex align-items-center justify-content-start w-1 pr-5" style={{ minWidth: "10rem" }}>
                                        <div className="flex flex-column">
                                            <div className="flex align-items-start justify-content-start">
                                                <label className="mousee-text font-x-small font-weight-semibold block">{t("overhead.page.commitment")}*</label>
                                            </div>
                                            <div className="flex align-items-center justify-content-center">
                                                <div className="p-inputgroup">
                                                    <span className="p-inputgroup-addon">
                                                        <i className="pi pi-percentage mousee-text-small font-xxx-small font-weight-regular"></i>
                                                    </span>
                                                    <InputNumber inputClassName="w-4rem" value={item?.commitment} max={100} onValueChange={(e) => updatePeopleAttribute(index, "commitment", e.value)} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex align-items-center justify-content-start">
                                        <Divider className="border-300" layout="vertical" />
                                    </div>

                                    <div className="flex align-items-center justify-content-center w-2" style={{ minWidth: "10rem" }}>
                                        <div className="flex flex-column">
                                            <div className="flex align-items-center justify-content-center">
                                                <label className="mousee-text-small font-small font-weight-bold">{t("label.monthly_hours")}</label>
                                            </div>
                                            <div className="flex align-items-center justify-content-center pt-3">
                                                <label className="mousee-text-small font-small font-weight-semibold">{item?.monthly_hours ? item?.monthly_hours : 0}</label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex align-items-center justify-content-center w-2" style={{ minWidth: "10rem" }}>
                                        <div className="flex flex-column">
                                            <div className="flex align-items-center justify-content-center">
                                                <label className="mousee-text-small font-small font-weight-bold">{t("label.weekly_hours")}</label>
                                            </div>
                                            <div className="flex align-items-center justify-content-center pt-3">
                                                <label className="mousee-text-small font-small font-weight-semibold">{item?.weekly_hours ? item?.weekly_hours : 0}</label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex align-items-center justify-content-start" style={{ minWidth: "5rem" }}>
                                        <Button
                                            icon={<Icon icon="ph:trash-light" className="" style={{ fontSize: "20px" }} />}
                                            className="p-button-text p-button-secondary"
                                            onClick={() => {
                                                const newPeople = formik?.values?.people?.filter((_, item_index) => item_index !== index);
                                                updateTenantField("people", newPeople);
                                                calculateOverhead(formik.values.weekly_hours, formik.values.business_days, newPeople, totalUsers);
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="flex align-items-center justify-content-start min-w-full">
                                    <Divider className="border-300" layout="horizontal" />
                                </div>
                            </div>
                        );
                    })}
            </div>
        );
    };

    const getTotalFTE = (people, totalUsers) => {
        const fteInd = people && people?.length ? people?.length : 0;
        const fteOps = totalUsers ? totalUsers : 0;

        return fteInd + fteOps;
    };

    const calculateOverhead = (weeklyHoursParam, businessDaysParam, peopleParam, totalUsersParam) => {
        weeklyHoursParam = weeklyHoursParam ? weeklyHoursParam : formik?.values?.weekly_hours;
        businessDaysParam = businessDaysParam ? businessDaysParam : formik?.values?.business_days;
        peopleParam = peopleParam ? peopleParam : formik?.values?.people;
        totalUsersParam = totalUsersParam ? totalUsersParam : totalUsers ? totalUsers : 0;

        if (weeklyHoursParam && businessDaysParam && peopleParam?.length > 0) {
            const dailyHours = weeklyHoursParam / 5;
            const totalPeople = peopleParam?.length + totalUsersParam;
            const hoursPerYear = dailyHours * businessDaysParam;

            const totalHoursFTE = totalPeople * hoursPerYear;

            if (peopleParam && peopleParam?.length > 0) {
                const newPeople = [...peopleParam];

                let totalHoursPerYear = 0;

                newPeople.forEach((item) => {
                    if (objectHasKeys(item)) {
                        if (item?.commitment) {
                            const itemHoursPerYear = Math.ceil(hoursPerYear * (item?.commitment / 100));

                            totalHoursPerYear += itemHoursPerYear;

                            item.monthly_hours = Math.ceil(itemHoursPerYear / 12);
                            item.weekly_hours = Math.ceil(item.monthly_hours / 4);
                        } else {
                            item.weekly_hours = 0;
                            item.monthly_hours = 0;
                        }
                    }
                });
                if (totalHoursPerYear && totalHoursFTE) {
                    setCalculatedOverhead(Math.ceil((totalHoursPerYear / totalHoursFTE) * 100));
                } else {
                    setCalculatedOverhead(0);
                }

                updateTenantField("people", newPeople);
            }
        } else {
            setCalculatedOverhead(0);
        }
    };

    const updateTenantField = (fieldName, fieldValue) => {
        let newData = formik.values;
        updateObjectProperty(newData, fieldName, fieldValue);

        if (!isEqual(newData, originalTenant)) {
            formik.setFieldValue(fieldName, fieldValue);
        }

        setChangesDetected(!isEqual(newData, originalTenant));
    };

    const saveAndLeave = () => {
        formik.handleSubmit();
        confirmNavigation();
    };

    if (loading) return <BlockUI blocked={loading} fullScreen template={<i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }}></i>}></BlockUI>;

    return checkComponentPermissions(
        <div className="grid page-layout">
            <ConfirmDialog
                visible={showPrompt}
                onHide={cancelNavigation}
                message={t("message.if_you_leave_this_page_the_changes_you_have_made_will_be_lost")}
                header={t("label.save_changes_question")}
                contentClassName="pt-5"
                acceptLabel={t("label.save_and_leave")}
                rejectLabel={t("label.leave_without_save")}
                icon="pi pi-exclamation-triangle"
                accept={saveAndLeave}
                reject={confirmNavigation}
            />
            <Toast ref={toast} />
            {/* <div className="xl:col-offset-1"></div> */}

            <div className="col-12 xl:col-12">
                <div className="grid">
                    <div className="col-12">
                        <div className="flex justify-content-between align-content-left flex-wrap">
                            <div className="flex align-items-center justify-content-left">
                                <label className="mousee-text font-large font-weight-semibold">{t("overhead.page.fte_configuration_fte")}</label>
                            </div>
                            <div className="flex align-items-center justify-content-end">
                                <Button type="submit" disabled={!changesDetected} label={t("label.save_changes")} className="p-button" onClick={() => formik.handleSubmit()} />
                            </div>
                        </div>
                    </div>

                    <div className="col-12 pt-5">
                        <div className="grid">
                            <div className="col-6 xl:col-3">
                                <div className="flex flex-column min-h-full justify-content-center">
                                    <div className="flex align-items-start justify-content-start">
                                        <label className="mousee-text font-x-small font-weight-semibold block pl-2">{t("label.business_days")}*</label>
                                    </div>
                                    <div className="flex align-items-center justify-content-center">
                                        <div className="p-inputgroup">
                                            <span className="p-inputgroup-addon">
                                                <span className="mousee-text-small font-xx-small font-weight-regular">{capitalizeWord(t("label.days"))}</span>
                                            </span>
                                            <InputNumber
                                                value={formik?.values?.business_days}
                                                min={0}
                                                onValueChange={(e) => {
                                                    updateTenantField("business_days", e.value);
                                                    calculateOverhead(formik.values.weekly_hours, e.value, formik.values.people, totalUsers);
                                                }}
                                                inputClassName="max-w-5rem"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex align-items-center justify-content-start">
                                        <label className="mousee-text-small font-xx-small font-weight-regular block pl-1">{t("label.enter_working_days_per_year")}</label>
                                    </div>
                                </div>
                            </div>
                            <div className="col-6 xl:col-3">
                                <div className="flex flex-column min-h-full justify-content-center">
                                    <div className="flex align-items-start justify-content-start">
                                        <label className="mousee-text font-x-small font-weight-semibold block pl-2">{t("label.weekly_hours")}*</label>
                                    </div>
                                    <div className="flex align-items-center justify-content-center">
                                        <div className="p-inputgroup">
                                            <span className="p-inputgroup-addon">
                                                <span className="mousee-text-small font-xx-small font-weight-regular">{t("label.hours")}</span>
                                            </span>
                                            <InputNumber
                                                value={formik?.values?.weekly_hours}
                                                min={0}
                                                onValueChange={(e) => {
                                                    updateTenantField("weekly_hours", e.value);
                                                    calculateOverhead(e.value, formik.values.business_days, formik.values.people, totalUsers);
                                                }}
                                                inputClassName="max-w-5rem"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex align-items-center justify-content-start">
                                        <label className="mousee-text-small font-xx-small font-weight-regular block pl-1">{t("label.enter_working_hours_per_week")}</label>
                                    </div>
                                </div>
                            </div>

                            <div className="col-12 xl:col-6">
                                <div className="flex flex-column min-w-full min-h-full justify-content-center">
                                    <div className="flex align-items-center justify-content-center">
                                        <div className="grid min-w-full fte-box">
                                            <div className="col-6 md:col">
                                                <div className="flex flex-column min-h-full justify-content-center">
                                                    <div className="flex align-items-center justify-content-center">
                                                        <label className="mousee-text-small font-small font-weight-bold">{t("overhead.page.fte_ind")}</label>
                                                    </div>
                                                    <div className="flex align-items-center justify-content-center pt-2">
                                                        <label className="mousee-text-small font-small font-weight-semibold">{formik?.values?.people?.length ? formik?.values?.people?.length : 0}</label>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6 md:col">
                                                <div className="flex flex-column min-h-full justify-content-center">
                                                    <div className="flex align-items-center justify-content-center">
                                                        <label className="mousee-text-small font-small font-weight-bold">{t("overhead.page.fte_ops")}</label>
                                                    </div>
                                                    <div className="flex align-items-center justify-content-center pt-2">
                                                        <label className="mousee-text-small font-small font-weight-semibold">{totalUsers ? totalUsers : 0}</label>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6 md:col">
                                                <div className="flex flex-column min-h-full justify-content-center">
                                                    <div className="flex align-items-center justify-content-center">
                                                        <label className="mousee-text-small font-small font-weight-bold">{t("overhead.page.fte_total_hours")}</label>
                                                    </div>
                                                    <div className="flex align-items-center justify-content-center pt-2">
                                                        <label className="mousee-text-small font-small font-weight-semibold">{getTotalFTE(formik?.values?.people, totalUsers)}</label>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6 md:col">
                                                <div className="flex flex-column min-h-full justify-content-center">
                                                    <div className="flex align-items-start justify-content-center md:justify-content-start">
                                                        <label className="mousee-text font-x-small font-weight-semibold block pl-2 overhead-label">{t("label.overhead")}*</label>
                                                    </div>
                                                    <div className="flex align-items-start justify-content-center md:justify-content-start">
                                                        <InputNumber
                                                            id="overhead"
                                                            name="overhead"
                                                            value={formik.values.overhead}
                                                            inputClassName="mousee-text font-small font-weight-light max-w-7rem overhead-label"
                                                            suffix="%"
                                                            onValueChange={(e) => {
                                                                updateTenantField("overhead", e.value);
                                                            }}
                                                            locale={i18n.language.split("-")[0]}
                                                            min={0}
                                                            maxFractionDigits={0}
                                                        />
                                                    </div>
                                                    <div className="flex align-items-start justify-content-center md:justify-content-start">
                                                        <label className="mousee-text-small font-x-small font-weight-regular">{`${calculatedOverhead}% (${t("label.recommended")})`}</label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex align-items-center justify-content-end">
                                        <label className="mousee-text font-x-small font-weight-regular" style={{ color: "var(--primary-color)" }}>
                                            {t("overhead.page.percentage_that_will_be_added_to_an_employees_costs")}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 pt-5">
                        <label className="mousee-text font-large font-weight-semibold">{t("tenant.page.topbar.menu.item.overhead")}</label>
                    </div>

                    <div className="col-12 pt-0">
                        <label className="mousee-text font-small font-weight-ultralight">
                            {t("overhead.page.overhead_are_the_indirect_costs_related_to_the_operation_and_administration_of_the_equipment_necessary_to_carry_out_the_project_to_calculate_it_we_must_assign_a_proportional_part_of_these_costs_to_each_project")}
                        </label>
                    </div>

                    <div className="col-12">
                        <Divider className="border-300" />
                    </div>

                    <div className="col-12 p-0">{peopleTemplate(formik?.values?.people)}</div>

                    <div className="col-12">
                        <Button
                            icon={"pi pi-plus-circle"}
                            label={t("overhead.page.add_people")}
                            type="button"
                            disabled={formik?.values?.people?.length > 0 && formik?.values?.people[formik?.values?.people?.length - 1]?.full_name.trim() === ""}
                            className="p-button-outlined"
                            onClick={() => addPeople()}
                        />
                    </div>
                </div>
            </div>
        </div>,
        ["view_tenant"],
        null,
        <Forbidden />
    );
};
export default Overhead;
